<template>

    <div class="header-wrapper txt-center">
      <div class="h1">{{ t('pageTitle') }}</div>
    </div>

    <div class="filters-grid border-filters">
      <template v-for="filter in filtersConfig" :key="filter.key">
        <MultiSelectFilter
            v-if="filter.type === 'multi-select'"
            v-model="filtersState[filter.stateKey]"
            :label="t(filter.labelKey)"
            :options="filter.options"
            :placeholder="t('form.select')"
        />

        <CheckboxFilter
            v-else-if="filter.type === 'checkbox'"
            v-model="filtersState[filter.stateKey]"
            :label="t(filter.labelKey)"
        />

        <div v-else-if="filter.type === 'date'" class="form-item form-calendar span-1">
          <label class="calendar-label">{{ t(filter.labelKey) }}</label>

          <div class="calendar-field">
            <Datepicker
                v-model="orderDatePickerValue"
                :range="orderDateFilterMode === 'range'"
                class="calendar-input"
                :placeholder="t(filter.placeholderKey)"
                :enable-time-picker="false"
                auto-apply
                format="dd.MM.yyyy"
                locale="ru"
            />
            <span class="icon-calendar"></span>
          </div>
        </div>

        <div v-else-if="filter.type === 'search'" class="filters-search span-2">
          <FieldSearchFilter
              v-model="filtersState[filter.stateKey]"
              :label="t(filter.labelKey)"
              :placeholder="t(filter.placeholderKey)"
              :fields="filter.fields"
          />
        </div>
      </template>
    </div>

    <div v-if="ordersLoading" class="table-group">
      <div class="table-loader"></div>
    </div>

    <div v-if="ordersError" class="mc-error">{{ ordersError }}</div>

    <div v-if="ordersLoaded" class="table-group" ref="tableGroup">

      <!-- Клон шапки для «прилипания» к верху окна -->
      <div class="table-sticky-header" v-if="stickyVisible" :style="stickyHeaderStyle">
        <div class="table-sticky-inner" :style="{ transform: `translateX(${-scrollLeft}px)` }">
          <table class="table editable-table has-special-drop"
                 :style="{ width: stickyTableWidth + 'px' }">
            <colgroup>
              <col v-for="(w, i) in columnWidths" :key="i" :style="{ width: w + 'px' }" />
            </colgroup>
            <thead class="table-thead">
            <tr>
              <th
                  v-for="column in tableColumns"
                  :key="column.key"
                  :class="column.class"
              >
                <template v-if="column.sortKey">
                  <button class="sort-btn" type="button" @click="setSort(column.sortKey)">
                    {{ t(column.labelKey) }}
                    <span class="sort-icons" aria-hidden="true">
                      <span :class="['sort-up', { active: sortKey === column.sortKey && sortDir === 'asc' }]"></span>
                      <span :class="['sort-down', { active: sortKey === column.sortKey && sortDir === 'desc' }]"></span>
                    </span>
                  </button>
                </template>
              </th>
            </tr>
            </thead>
          </table>
        </div>
      </div>

      <button
          v-if="showScrollLeft"
          class="table-button-prev"
          type="button"
          :style="floatingButtonStyle"
          @mouseenter="startAutoScroll('left')"
          @mouseleave="stopAutoScroll"
          @mousedown.prevent="startAutoScroll('left')"
          @mouseup="stopAutoScroll"
          @touchstart.prevent="startAutoScroll('left')"
          @touchend="stopAutoScroll"
      >
        <svg class="svg-left">
          <use href="/img/sprite.svg#svg-left"></use>
        </svg>
      </button>

      <button
          v-if="showScrollRight"
          class="table-button-next"
          type="button"
          :style="floatingButtonStyle"
          @mouseenter="startAutoScroll('right')"
          @mouseleave="stopAutoScroll"
          @mousedown.prevent="startAutoScroll('right')"
          @mouseup="stopAutoScroll"
          @touchstart.prevent="startAutoScroll('right')"
          @touchend="stopAutoScroll"
      >
        <svg class="svg-right">
          <use href="/img/sprite.svg#svg-right"></use>
        </svg>
      </button>

      <div
          id="tableScroll"
          ref="tableScroll"
          class="table-wrapper scroll-x has-special-drop"
          @scroll="updateScrollButtons"
      >
        <table class="table order-table">
          <thead class="table-thead">
          <tr>
            <th
                v-for="column in tableColumns"
                :key="column.key"
                :class="column.class"
            >
              <template v-if="column.sortKey">
                <button class="sort-btn" type="button" @click="setSort(column.sortKey)">
                  {{ t(column.labelKey) }}
                  <span class="sort-icons" aria-hidden="true">
                      <span :class="['sort-up', { active: sortKey === column.sortKey && sortDir === 'asc' }]"></span>
                      <span :class="['sort-down', { active: sortKey === column.sortKey && sortDir === 'desc' }]"></span>
                    </span>
                </button>
              </template>
            </th>
          </tr>
          </thead>

          <tbody class="table-tbody">
          <tr v-for="(order, index) in filteredOrders" :key="order.xml_id || index">
            <td class="first-col">
              <button
                  v-if="order.has_refusal_positions"
                  class="btn-note has-tooltip has-tooltip-right"
                  type="button"
              >
                <span class="btn-tooltip">{{ t('tooltip.refusalPositions') }}</span>
              </button>
            </td>

            <td class="cell-order-number">
                <span :class="order.color ? 'box-' + order.color : 'box-grey'">
                  {{ order.number }}
                </span>
            </td>

            <td class="cell-date">{{ order.date }}</td>
            <td class="cell-invoice">{{ order.invois }}</td>
            <td class="cell-invoice-date">{{ order.invois_date }}</td>
            <td class="cell-edit">{{ order.update_date }}</td>
            <td class="cell-company">{{ order.filial_name }}</td>
            <td class="cell-contractor">{{ order.partner_name }}</td>
            <td class="cell-manager">{{ order.user_last_name }} {{ order.user_name }}</td>

            <td class="cell-price cell-price-amount">
              <div class="price-drop">
                <span class="price-value">{{ order.summa ?? '-' }}</span>
              </div>
            </td>

            <td class="cell-price cell-deficit">
              <div class="price-drop">
                <span class="price-value">{{ order.summa_work ?? '-' }}</span>
              </div>
            </td>

            <td class="cell-status">{{ order.status }}</td>

            <td class="cell-options">
              <router-link :to="{ name: 'order', params: { id: order.id } }" class="btn-view has-tooltip">
                <span class="btn-tooltip">{{ t('tooltip.view') }}</span>
              </router-link>
            </td>

            <td class="cell-options">
              <router-link v-if="(user.role=='manager' || user.role=='admin') && order.status=='Черновик'" :to="{ name: 'order', params: { id: order.id } }" class="btn-edit has-tooltip">
                <span class="btn-tooltip">{{ t('tooltip.edit') }}</span>
              </router-link>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

import MultiSelectFilter from '@/components/filters/MultiSelectFilter.vue';
import FieldSearchFilter from '@/components/filters/FieldSearchFilter.vue';
import CheckboxFilter from '@/components/filters/CheckboxFilter.vue';

import { isDateInRange, getDateRangeFromPicker } from '@/utils/dateUtils';
import { sortArray, getSortValue } from '@/utils/sortUtils';
import scrollManagerMixin from '@/mixin/scrollManagerMixin';

export default {
  name: 'Main',
  components: {
    Datepicker,
    MultiSelectFilter,
    FieldSearchFilter,
    CheckboxFilter,
  },
  mixins: [scrollManagerMixin],
  setup() {
    const { t, locale } = useI18n();

    const changeLanguage = (lang) => {
      locale.value = lang;
      localStorage.setItem('locale', lang);
    };

    return {
      t,
      locale,
      changeLanguage
    };
  },
  data: () => ({
    currentLang: 'ru',

    sortKey: 'number',
    sortDir: 'asc',

    filtersState: {
      selectedStatuses: [],
      selectedSuppliers: [],
      selectedManagers: [],
      selectedOffices: [],
      showRefusalPositionsOnly: false,
      invoiceOrderSearch: '',
    },

    orderDateFilterMode: 'range',
    orderDate: null,
    orderDateRange: null,
  }),

  created() {
    this.fetchOrders();
  },

  watch: {
    ordersLoaded(val) {
      if (val) {
        this.updateScrollOnDataChange();
      }
    },
    filteredOrders() {
      this.updateScrollOnDataChange();
    },
  },

  computed: {
    ...mapGetters('orders', [
      'orders',
      'ordersLoading',
      'ordersError',
      'ordersLoaded',
    ]),
    ...mapGetters( [
      'user',
    ]),
    filtersConfig() {
      return [
        {
          key: 'status',
          type: 'multi-select',
          labelKey: 'filters.status',
          stateKey: 'selectedStatuses',
          options: this.statusOptions,
        },
        {
          key: 'supplier',
          type: 'multi-select',
          labelKey: 'filters.supplier',
          stateKey: 'selectedSuppliers',
          options: this.supplierOptions,
        },
        {
          key: 'manager',
          type: 'multi-select',
          labelKey: 'filters.manager',
          stateKey: 'selectedManagers',
          options: this.managerOptions,
        },
        {
          key: 'order-date',
          type: 'date',
          labelKey: 'filters.orderDate',
          placeholderKey: 'filters.choose',
        },
        {
          key: 'office',
          type: 'multi-select',
          labelKey: 'filters.office',
          stateKey: 'selectedOffices',
          options: this.officeOptions,
        },
        {
          key: 'refusal',
          type: 'checkbox',
          labelKey: 'filters.refusal',
          stateKey: 'showRefusalPositionsOnly',
        },
        {
          key: 'search',
          type: 'search',
          labelKey: 'filters.search',
          placeholderKey: 'filters.searchorder',
          stateKey: 'invoiceOrderSearch',
          fields: ['invois', 'number'],
        },
      ];
    },

    tableColumns() {
      return [
        { key: 'first-col', class: 'first-col' },
        { key: 'number', class: 'cell-order-number', sortKey: 'number', labelKey: 'table.number' },
        { key: 'date', class: 'cell-date', sortKey: 'date', labelKey: 'table.date' },
        { key: 'invois', class: 'cell-invoice', sortKey: 'invois', labelKey: 'table.invois' },
        { key: 'invois_date', class: 'cell-invoice-date', sortKey: 'invois_date', labelKey: 'table.invoisDate' },
        { key: 'update_date', class: 'cell-edit', sortKey: 'update_date', labelKey: 'table.updateDate' },
        { key: 'filial_name', class: 'cell-company', sortKey: 'filial_name', labelKey: 'table.company' },
        { key: 'partner_name', class: 'cell-contractor', sortKey: 'partner_name', labelKey: 'table.supplier' },
        { key: 'user_name', class: 'cell-manager', sortKey: 'user_name', labelKey: 'table.manager' },
        { key: 'summa', class: 'cell-price', sortKey: 'summa', labelKey: 'table.amount' },
        { key: 'summa_work', class: 'cell-deficit', sortKey: 'summa_work', labelKey: 'table.deficit' },
        { key: 'status', class: 'cell-status', sortKey: 'status', labelKey: 'table.status' },
        { key: 'view', class: 'cell-options' },
        { key: 'edit', class: 'cell-options' },
      ];
    },

    orderDatePickerValue: {
      get() {
        return this.orderDateFilterMode === 'range' ? this.orderDateRange : this.orderDate;
      },
      set(value) {
        if (this.orderDateFilterMode === 'range') {
          this.orderDateRange = value;
          if (Array.isArray(value) && value.length === 2 && !value[0] && !value[1]) {
            this.orderDateRange = null;
          }
        } else {
          this.orderDate = value;
          if (!value) {
            this.orderDate = null;
          }
        }
      },
    },

    sortedOrders() {
      return sortArray(this.orders || [], this.sortKey, this.sortDir);
    },

    statusOptions() {
      return this.buildUniqueOptions(this.orders, order => order?.status);
    },

    supplierOptions() {
      return this.buildUniqueOptions(this.orders, order => order?.partner_name);
    },

    managerOptions() {
      return this.buildUniqueOptions(this.orders, order => `${order?.user_last_name || ''} ${order?.user_name || ''}`.trim());
    },

    officeOptions() {
      return this.buildUniqueOptions(this.orders, order => order?.filial_name);
    },

    filteredOrders() {
      const search = this.normalizeSearch(this.filtersState.invoiceOrderSearch);
      const dateRange = getDateRangeFromPicker(this.orderDatePickerValue, this.orderDateFilterMode);

      return this.sortedOrders.filter(order => {
        const matchStatus = !this.filtersState.selectedStatuses.length || this.filtersState.selectedStatuses.includes(order.status);
        const matchSupplier = !this.filtersState.selectedSuppliers.length || this.filtersState.selectedSuppliers.includes(order.partner_name);
        const managerValue = `${order?.user_last_name || ''} ${order?.user_name || ''}`.trim();
        const matchManager = !this.filtersState.selectedManagers.length || this.filtersState.selectedManagers.includes(managerValue);
        const matchOffice = !this.filtersState.selectedOffices.length || this.filtersState.selectedOffices.includes(order.filial_name);
        const matchRefusalPositions = !this.filtersState.showRefusalPositionsOnly || Boolean(order?.has_refusal_positions);

        const matchInvoiceOrder =
            !search ||
            ['invois', 'number'].some(field => this.normalizeSearch(order?.[field]).includes(search));

        const matchDate = isDateInRange(order?.date, dateRange);

        return matchStatus && matchSupplier && matchManager && matchOffice && matchRefusalPositions && matchInvoiceOrder && matchDate;
      });
    },
  },

  methods: {
    ...mapActions('orders', [
      'fetchOrders',
    ]),

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        return;
      }

      this.sortKey = key;
      this.sortDir = 'asc';
    },

    normalizeSearch(value) {
      return String(value ?? '').trim().toLowerCase();
    },

    buildUniqueOptions(source, getter) {
      const unique = new Map();

      (source || []).forEach(item => {
        const value = getter(item);
        if (value === null || value === undefined || value === '') return;

        if (!unique.has(value)) {
          unique.set(value, {
            value,
            label: String(value),
          });
        }
      });

      return Array.from(unique.values());
    },

  },
};
</script>